import { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';

import EnhancedTable, { EnhancedTableProps, EnhancedTableRow } from '#components/EnhancedTable';
import {
  ALL_TIME_RANGE_PRESET,
  TimeRangeValue,
  resolveDateRangeParam,
} from '#components/time-range.utils';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import {
  useGetVendorsQuery,
  useDeleteVendorMutation,
} from '#services/vendor';
import {
  KEY_CODES,
} from '#utils/constants';

import URLSearchParamsEx from '#utils/url-ex';
import { formatDate } from '#common/utils';
import {
  setDateRange,
  setFilter,
  setFilterForQuery,
  setOrder,
  setOrderBy,
  setPageIndex,
  setPageSize,
} from './list.slice';

interface VendorRow {
  id: string | number;
  name: string;
  description?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  productCount: number;
  [key: string]: unknown;
}

interface VendorListSlice {
  dateRange: TimeRangeValue;
  filter: string;
  filterForQuery: string;
  order: 'asc' | 'desc';
  orderBy: string;
  pageIndex: number;
  rows: EnhancedTableRow[];
  rowCount: number;
  pageSize: number;
}

export default function VendorList() {
  const {
    showBackdrop,
    hideBackdrop,
    showDialog,
    hideDialog,
    setDialogData,

    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [deleteVendor] = useDeleteVendorMutation();

  const filter = useSelector((state: { vendorList: VendorListSlice }) => state.vendorList.filter);
  const dateRange = useSelector((state: { vendorList: VendorListSlice }) => state.vendorList.dateRange);
  const filterForQuery = useSelector((state: { vendorList: VendorListSlice }) => state.vendorList.filterForQuery);
  const order = useSelector((state: { vendorList: VendorListSlice }) => state.vendorList.order);
  const orderBy = useSelector((state: { vendorList: VendorListSlice }) => state.vendorList.orderBy);
  const pageIndex = useSelector((state: { vendorList: VendorListSlice }) => state.vendorList.pageIndex);
  const rows = useSelector((state: { vendorList: VendorListSlice }) => state.vendorList.rows);
  const rowCount = useSelector((state: { vendorList: VendorListSlice }) => state.vendorList.rowCount);
  const pageSize = useSelector((state: { vendorList: VendorListSlice }) => state.vendorList.pageSize);
  const [isInitialMount, setIsInitialMount] = useState(true);

  const query = useGetVendorsQuery({
    dateRange: resolveDateRangeParam('createdAt', dateRange),
    order,
    orderBy,
    filter: filterForQuery,
    page: pageIndex,
    pageSize,
  }, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!isInitialMount) return;

    const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);
    if (urlParams.values.filter !== undefined) {
      dispatch(setFilter(urlParams.values.filter as string));
      dispatch(setFilterForQuery(urlParams.values.filter as string));
    }
    if (urlParams.values.order !== undefined) {
      dispatch(setOrder(urlParams.values.order as string));
    }
    if (urlParams.values.orderBy !== undefined) {
      dispatch(setOrderBy(urlParams.values.orderBy as string));
    }
    if (urlParams.values.pageSize !== undefined) {
      dispatch(setPageSize(urlParams.values.pageSize as unknown as number));
    }

    setIsInitialMount(false);
  }, [location.search, isInitialMount, dispatch]);

  useEffect(() => {
    simpleSnackbar(query as unknown as Parameters<typeof simpleSnackbar>[0]);
  }, [query.error]);

  const tableHeaders: EnhancedTableProps['headers'] = [{
    id: 'name',
    label: 'Vendor',
    renderer: (row, idx) => {
      const vendor = row as unknown as VendorRow;
      const vendorIndex = pageIndex * pageSize + idx + 1;
      const listParams = new URLSearchParamsEx({
        order,
        orderBy,
        filter: filterForQuery,
        pageSize,
      }).toString();
      const vendorParams = new URLSearchParams({
        vendorId: vendor.id,
        vendorIndex,
      } as unknown as Record<string, string>).toString();
      const url = `/management/product/vendor/edit?${listParams}&${vendorParams}`;
      return (
        <Box>
          <Link
            href={url}
            onClick={(e) => {
              e.preventDefault();
              void navigate(url);
            }}
          >
            {vendor.name}
          </Link>
        </Box>
      );
    },
  }, {
    id: 'productCount',
    label: 'Products',
  }, {
    id: 'description',
    label: 'Description',
  }, {
    id: 'notes',
    label: 'Notes',
  }, {
    id: 'createdAt',
    label: 'Created At',
    renderer: (row) => formatDate((row as unknown as VendorRow).createdAt as string, '-'),
  }, {
    id: 'updatedAt',
    label: 'Last Updated',
    renderer: (row) => formatDate((row as unknown as VendorRow).updatedAt as string, '-'),
  }, {
    id: 'action',
    label: 'Action',
    sortable: false,
    renderer: (row) => {
      const vendor = row as unknown as VendorRow;
      const handleDeleteVendor = () => {
        setDialogData({
          title: 'Delete',
          text: 'Are you sure you want to delete this vendor?',
          disagree: 'Cancel',
          agree: 'Delete',
          onDisagree: () => hideDialog(),
          onAgree: () => {
            hideDialog();
            showBackdrop();

            void deleteVendor({
              order,
              orderBy,
              filter: filterForQuery,
              page: pageIndex,
              pageSize,
              vendorId: vendor.id,
            }).then((res) => {
              simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], `Vendor "${vendor.name}" was successfully deleted.`);
            }).finally(() => {
              hideBackdrop();
            });
          },
        });
        showDialog();
      };

      const disabled = (vendor.productCount > 0);
      if (disabled) {
        return (
          <Box>
            <Tooltip title="Deletion of a vendor is only possible when it contains no products.">
              <span>
                <Link
                  component="button"
                  className="disabled-link"
                >
                  Delete
                </Link>
              </span>
            </Tooltip>
          </Box>
        );
      }

      return (
        <Box>
          <Link
            component="button"
            onClick={handleDeleteVendor}
          >
            Delete
          </Link>
        </Box>
      );
    },
  }];

  const tableProps: EnhancedTableProps = {
    name: 'vendors',

    headers: tableHeaders,

    enableSort: true,

    order,
    setOrder,
    orderBy,
    setOrderBy,

    pageSize,
    pageIndex,
    setPageIndex,
    setPageSize,
    rowCount,

    timeRange: {
      value: dateRange,
      onChange: (value) => dispatch(setDateRange(value)),
      columnLabel: 'Created',
      defaultPreset: ALL_TIME_RANGE_PRESET,
      entityLabel: 'vendors',
    },

    rows,
  };

  return (
    <>
      <Box sx={{ typography: 'h7' }}>
        Vendors
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Paper
          elevation={0}
          variant="outlined"
          square
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: 400,
          }}
        >
          <IconButton
            type="button"
            sx={{ p: '10px' }}
            aria-label="search"
            onClick={() => dispatch(setFilterForQuery(filter))}
          >
            <SearchIcon />
          </IconButton>
          <InputBase
            sx={{ ml: 1, flex: 1 }}
            placeholder="Filter Vendors"
            value={filter}
            onChange={(e) => {
              dispatch(setFilter(e.target.value));
            }}
            onKeyUp={(e) => {
              if (e.keyCode === KEY_CODES.ENTER || !filter.length) {
                dispatch(setFilterForQuery(filter));
              }
            }}
          />
        </Paper>
      </Box>

      <EnhancedTable
        {...tableProps}
      />
    </>
  );
}
